import PropTypes from 'prop-types';

import map from 'lodash/fp/map';

import styled from '@emotion/styled';

import { breakpoints } from 'Components/ThemeProvider';
import Typography from 'Components/Typography';

import ConsultationCTA from 'Containers/ConsultationCTA';

import howWorksContent from 'assets/content/howWorksContent';

import useMedia from 'utils/useMedia';

import BlockLayout from './BlockLayout';

const Title = styled(Typography)`
  ${props => props.theme.breakpoints.match('sm')} {
    width: 60%;
  }
`;

const StepsContainer = styled.div`
  margin: ${props => props.theme.spacing.s48}px auto 0 auto;
  width: 100%;
  counter-reset: step;

  ${props => props.theme.breakpoints.up('sm')} {
    margin-top: ${props => props.theme.spacing.s64}px;
  }

  ${props => props.theme.breakpoints.up('md')} {
    display: flex;
    justify-content: space-between;
  }
`;

const StepBlock = styled.div`
  display: grid;
  grid-template-columns: auto minmax(200px, 300px);
  justify-content: center;
  column-gap: ${props => props.theme.spacing.s40}px;
  margin: 0 auto;
  &:not(:last-child) {
    margin-bottom: ${props => props.theme.spacing.s64}px;
  }

  ${props => props.theme.breakpoints.up('sm')} {
    column-gap: ${props => props.theme.spacing.s72}px;
  }

  ${props => props.theme.breakpoints.up('md')} {
    display: block;
    max-width: 300px;
  }
`;

const StepImage = styled.img`
  display: block;
  margin-bottom: ${props => props.theme.spacing.s32}px;
  width: 75px;
  height: 90px;

  ${props => props.theme.breakpoints.up('md')} {
    height: 96px;
  }
`;

/**
 * Helps to keep its content align with sibling blocks
 */
const StepTitleWrapper = styled.div`
  position: relative;
  border-bottom: 1px solid white;

  ${props => props.theme.breakpoints.up('md')} {
    height: 56px;
  }
`;

const StepTitle = styled(Typography)`
  width: 100%;
  padding-right: 20%;
  &::after {
    position: absolute;
    right: 0;
    counter-increment: step;
    content: counter(step, decimal-leading-zero);
    font-family: ${props => props.theme.typography.simplonNorm};
    font-size: 18px;
  }
  ${props => props.theme.breakpoints.up('md')} {
    position: absolute;
    bottom: 0;
  }
`;

const StepParagraph = styled(Typography)`
  margin-top: ${props => props.theme.spacing.s16}px;
`;

const StyledConsultationCTA = styled(ConsultationCTA)`
  margin: ${props => props.theme.spacing.s64}px auto 0 auto;
  ${props => props.theme.breakpoints.up('md')} {
    margin: 0 auto;
  }
`;

const HowItWorks = ({ cta, dataFrom, dataTestId, isSkincare }) => {
  const { title, elements } = howWorksContent;
  const isMobile = useMedia(`(max-width: ${breakpoints.sm - 1}px)`);
  return (
    <BlockLayout backgroundColor="#76796A" contentWidth="veryWide" dataTestId={dataTestId}>
      <Title align={isMobile ? 'left' : 'center'} color="white" paragraph variant="h1r">
        {title}
      </Title>

      <StepsContainer>
        {map(
          ({ title: stepTitle, contentSkincare, contentHaircare, asset }) => (
            <StepBlock key={stepTitle}>
              <StepImage alt={asset.alt} src={asset.icon} />
              <div>
                <StepTitleWrapper>
                  <StepTitle bold color="white" paragraph variant="mono1">
                    {stepTitle}
                  </StepTitle>
                </StepTitleWrapper>
                <StepParagraph color="white" paragraph variant="p2">
                  {isSkincare ? contentSkincare : contentHaircare}
                </StepParagraph>
              </div>
            </StepBlock>
          ),
          elements
        )}
      </StepsContainer>

      {cta && (
        <StyledConsultationCTA dataFrom={dataFrom} fullWidth={isMobile} variant="whiteFull" />
      )}
    </BlockLayout>
  );
};

HowItWorks.propTypes = {
  cta: PropTypes.bool,
  dataFrom: PropTypes.string,
  dataTestId: PropTypes.string,
  isSkincare: PropTypes.bool,
};

HowItWorks.defaultProps = {
  cta: false,
  dataFrom: 'block-press',
  dataTestId: null,
  isSkincare: false,
};

export default HowItWorks;
